import { useEffect } from 'react'
import { Alert, Form, Modal, Select, Typography } from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { shortId } from '@/shared/lib/shortId'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { withCurrentOption } from '@/shared/ui/currentOption'
import { actorDisplayName } from '@/features/identity/actorName'
import { fetchAllUsers } from '@/features/identity/identityApi'
import { useOrgUnits } from '@/features/identity/useOrgUnits'
import { assignLead, assignTicket } from './crmApi'

/**
 * Mas'ul biriktirish — lid va murojaat uchun BITTA oyna.
 *
 * <h2>⚠️ NEGA TAHRIRLASH FORMASIGA QO'SHILMADI</h2>
 * Uch o'lchangan sabab, uchalasi ham serverdan:
 *
 * <ol>
 *   <li><b>Amal boshqa yo'ldan boradi va boshqa auditga tushadi.</b>
 *       `POST /{id}/assign` &rarr; `CRM_LEAD_ASSIGN` /
 *       `CRM_TICKET_ASSIGN`. Forma orqali yuborilsa u auditda "Lid
 *       tavsifi to'g'rilandi" bo'lib qolardi va "kim kimga
 *       biriktirdi" degan savolga javob beruvchi yozuv yo'qolardi.</li>
 *   <li><b>Tiketda amal YOPILGANDAN KEYIN ham ishlaydi.</b>
 *       `TicketServiceImpl.assign` holatni tekshirmaydi, `update` esa
 *       yopilganini rad etadi (`NotEditable`). Ikkalasini birlashtirish
 *       yopilgan tiketning egasini almashtirish imkonini
 *       YO'QOTARDI.</li>
 *   <li>⚠️ <b>Lidda amal TELEFONNI OCHISHNI talab qilmaydi.</b>
 *       `PUT /api/crm/leads/{id}` to'liq almashtirish bo'lgani uchun
 *       niqoblanmagan raqamni talab qiladi (`leadEditPlan`), `assign`
 *       esa faqat bitta ustunga tegadi. Birlashtirilsa `crm.lead.reveal`
 *       huquqi bo'lmagan sotuv boshlig'i mas'ulni umuman almashtira
 *       olmasdi.</li>
 * </ol>
 *
 * <p>Shuning uchun `assignTicket` (u ilgari CHAQIRUVCHISIZ o'lik kod
 * edi) va yangi `assignLead` — ikkalasi ham shu oynadan chaqiriladi.
 *
 * <h2>⚠️ RO'YXAT `user.read` TALAB QILADI</h2>
 * Xodimlar ro'yxati `identity` modulidan keladi. Huquq bo'lmasa
 * so'rov 403 berardi va tanlov BO'SH ko'rinardi — shuning uchun amal
 * bandi sahifada O'CHIQ bo'ladi va sababi yorlig'ida yoziladi
 * (`LeadsPage`/`TicketsPage`).
 *
 * <h2>⚠️ JORIY MAS'UL RO'YXATDA BO'LMASA HAM YO'QOLMAYDI</h2>
 * `withCurrentOption` — N8 qorovuli: antd mos yorliq topmasa xom
 * UUID ni chizadi va operator maydonni "bo'sh" deb o'qirdi.
 */
export type CrmAssignKind = 'lead' | 'ticket'

export interface CrmAssignTarget {
  id: string
  /** Ekranda ko'rinadigan nom — lid ismi yoki tiket mavzusi */
  title: string
  assignedTo: string | null | undefined
  /** Faqat tiket: joriy bo'lim */
  team?: { id: string; name: string } | null
  /** Faqat tiket: mas'ul almashsa qabul, tasdiq yoki boshlangan ish bekor bo'ladi */
  progressAtRisk?: boolean
}

interface CrmAssignModalProps {
  /** `null` — oyna yopiq */
  target: CrmAssignTarget | null
  kind: CrmAssignKind
  onCancel: () => void
  onDone: () => void
}

interface AssignValues {
  assignedTo?: string
  teamId?: string
}

export function CrmAssignModal({ target, kind, onCancel, onDone }: CrmAssignModalProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const [form] = Form.useForm<AssignValues>()
  const withTeam = kind === 'ticket'
  const selectedAssignee = Form.useWatch('assignedTo', form)

  const users = useQuery({
    queryKey: queryKeys.identity.users,
    queryFn: fetchAllUsers,
    enabled: target !== null && can('user.read'),
    staleTime: 5 * 60 * 1000,
  })
  const orgUnits = useOrgUnits(target !== null && withTeam)

  useEffect(() => {
    if (target === null) return
    form.setFieldsValue({
      assignedTo: target.assignedTo ?? undefined,
      teamId: target.team?.id ?? undefined,
    })
  }, [target, form])

  const assign = useMutation({
    // ⚠️ Javob ATAYLAB tashlanadi: lid va tiket ikki xil yozuv va ularni
    // bitta tipga zo'rlab keltirish faqat kesh uchun kerak bo'lardi —
    // ro'yxatni esa chaqiruvchi `onDone` da butunlay yangilaydi.
    mutationFn: async ({ assignedTo, teamId }: { assignedTo: string | null; teamId: string | null }) => {
      if (kind === 'lead') await assignLead(target!.id, assignedTo)
      else await assignTicket(target!.id, assignedTo, teamId)
    },
    onSuccess: onDone,
    // ⚠️ `onError` ATAYLAB yo'q — xato OYNANING ICHIDA chiziladi.
  })

  const dismiss = () => {
    assign.reset()
    onCancel()
  }

  const options = withCurrentOption(
    (users.data ?? []).map((u) => ({ value: u.id, label: actorDisplayName(u) })),
    target?.assignedTo ?? null,
    (value) => ({ value, label: shortId(value) }),
  )

  const staffedKinds = new Set(orgUnits.types.filter((type) => type.hasEmployees).map((type) => type.code))
  const currentTeam = target?.team ?? null
  const teamOptions = withCurrentOption(
    orgUnits.units
      .filter((unit) => unit.state === 'ACTIVE' && staffedKinds.has(unit.kind))
      .map((unit) => ({ value: unit.id, label: orgUnits.pathOf(unit.id) ?? unit.displayName })),
    currentTeam?.id ?? null,
    () => (currentTeam === null ? null : { value: currentTeam.id, label: currentTeam.name }),
  )

  const reassigning = (selectedAssignee ?? null) !== (target?.assignedTo ?? null)

  return (
    <Modal
      open={target !== null}
      title={t('crm.assign.title')}
      onCancel={dismiss}
      onOk={() =>
        // ⚠️ Bo'sh tanlov — BO'SHATISH buyrug'i, `undefined` emas:
        // server `assignedTo: null` ni ustunni tozalash deb o'qiydi.
        submitOnValid(form, (values) =>
          assign.mutate({ assignedTo: values.assignedTo ?? null, teamId: values.teamId ?? null }),
        )
      }
      confirmLoading={assign.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      {target ? (
        <Typography.Paragraph type="secondary">
          {t(`crm.assign.subtitle.${kind}`, { name: target.title })}
        </Typography.Paragraph>
      ) : null}

      {assign.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(assign.error, t)}
        />
      ) : null}

      {withTeam && target?.progressAtRisk && reassigning ? (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('crm.assign.resetWarning')} />
      ) : null}

      <Form<AssignValues> form={form} layout="vertical" preserve={false}>
        {withTeam ? (
          <Form.Item
            name="teamId"
            label={t('crm.assign.team')}
            extra={orgUnits.reason === 'no-permission' ? t('crm.assign.teamNoDirectory') : undefined}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              loading={orgUnits.loading}
              disabled={orgUnits.reason === 'no-permission'}
              options={teamOptions}
              placeholder={t('crm.assign.noTeam')}
            />
          </Form.Item>
        ) : null}
        <Form.Item
          name="assignedTo"
          label={t('crm.assign.field')}
          extra={t('crm.assign.clearHint')}
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            loading={users.isPending}
            options={options}
            placeholder={t('crm.assign.nobody')}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
